import { useEffect } from "react";
import type { FormInstance } from "antd";

/**
 * 浏览器自动填充同步 hook
 *
 * 浏览器/密码管理器在自动填充 input 时通常不会触发 React 的 onChange，
 * 导致 antd Form 的内部值不更新，校验时仍报"字段错误"。
 * 这里通过监听 input 上的 -webkit-autofill animationstart 事件，
 * 一旦检测到自动填充就把当前 DOM 值同步回 Form。
 *
 * 必须配合 tailwind.css 中的 @keyframes onAutoFillStart / onAutoFillCancel
 * 以及 input:-webkit-autofill 选择器使用。
 */
export function useAutofillSync(form: FormInstance | null | undefined, fieldNames: string[]) {
  useEffect(() => {
    if (!form) return;
    const fields = fieldNames.filter(Boolean);
    if (fields.length === 0) return;
    const handler = (e: Event) => {
      const ae = e as AnimationEvent;
      if (ae.animationName !== "onAutoFillStart" && ae.animationName !== "onAutoFillCancel") {
        return;
      }
      const target = e.target as HTMLInputElement;
      if (!target || !target.name) return;
      if (!fields.includes(target.name)) return;
      const value = target.value;
      const current = form.getFieldValue(target.name);
      if (value !== current) {
        form.setFieldsValue({ [target.name]: value });
        // 触发该字段的校验，让"两次输入不一致"等规则能基于新值重新计算
        try {
          form.validateFields([target.name]).catch(() => undefined);
        } catch {
          /* noop */
        }
      }
    };
    document.addEventListener("animationstart", handler, true);
    return () => {
      document.removeEventListener("animationstart", handler, true);
    };
  }, [form, fieldNames]);
}
